/**
 * serverClock
 *
 * A skew-corrected clock. The session-progress bar (and anything else that compares a server-issued timestamp against "now") must NOT trust the local machine's wall clock: user clocks are routinely tens of seconds off, which makes `Date.now() - serverTimestamp` wrong by that much. calculateProgress uses that difference as elapsed-in-stage, so a slow client clock made the bar start high and shrink.
 *
 * Fix: track an offset between the server clock and the local clock, measured from the `Date` response header that every orc/Cloud Run HTTP response carries, and expose serverNow() = Date.now() + offset. Because the anchor is the SERVER clock, every client converges on the same corrected elapsed, and a reload re-measures the offset from the next response — so it survives reloads and stays consistent across clients (the two hard requirements).
 *
 * Granularity note: the HTTP `Date` header is whole-seconds, so the offset is accurate to ~±0.5s — negligible for a ~60s progress bar. We keep the newest reading (clocks drift slowly; the latest sample is the most representative for round-trip-symmetric requests).
 */

let offsetMs = 0
let haveOffset = false

// Update the server/local offset from an HTTP `Date` header value (RFC 7231, whole seconds). No-op on a missing/unparseable header so a weird response can't corrupt the clock.
export const updateServerOffset = dateHeader => {
	if (!dateHeader) return
	const serverMs = Date.parse(dateHeader)
	if (Number.isNaN(serverMs)) return
	offsetMs = serverMs - Date.now()
	haveOffset = true
}

// Best current estimate of the server's wall clock. Falls back to the local clock until the first response has been seen (identical behavior to the old code for that brief window).
export const serverNow = () => Date.now() + offsetMs

// Current offset in ms (server − local), and whether it's been measured yet. For diagnostics/tests.
export const getServerOffsetMs = () => offsetMs
export const hasServerOffset = () => haveOffset
